import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";

const API_END_POINT = "/pb/admin/search";

// Results the picker asks for in one page. There is no way to reach page 2 from
// the member popover, so this number is the whole result set a user ever sees:
// a common surname sorted alphabetically can push the person they want past the
// end of it. Named once because the default and the request fallback below must
// never drift apart.
const CONTACT_SEARCH_LIMIT = 20;

export interface ContactFilter {
  search?: string;
  active_only?: number;
  type?: string[];
  limit: number;
  page?: number;
}

interface S2ContactResult {
  contact_id?: string;
  edumate_contact_id?: number;
  name?: string;
  email?: string;
  staff?: boolean;
  student?: boolean;
  carer?: boolean;
  staff_details?: string;
  student_details?: string;
  foreign_keys?: unknown;
}

interface MappedContact {
  _id: string | undefined;
  // Numeric Edumate ContactId. The owner / manager preset pickers feed this
  // into owner_contact_id / manager_contact_ids[] (the API contract is int).
  // ``_id`` is the ContactPersonal hex id and must NOT be coerced to a number.
  EdumateContactId: number | undefined;
  id?: string | undefined;
  Name: string | undefined;
  Email: string | undefined;
  Staff: boolean | undefined;
  Student: boolean | undefined;
  Carer: boolean | undefined;
  StaffDetails: string | undefined;
  StudentDetails: string | undefined;
  ForeignKeys: unknown;
  [key: string]: unknown;
}

// Map S2 response (snake_case) to S1 shape (PascalCase) expected by UI
const mapResponse = (results: S2ContactResult[]): MappedContact[] => {
  if (!Array.isArray(results)) return [];
  return results.map((r) => ({
    _id: r.contact_id,
    EdumateContactId: r.edumate_contact_id,
    Name: r.name,
    Email: r.email,
    Staff: r.staff,
    Student: r.student,
    Carer: r.carer,
    StaffDetails: r.staff_details,
    StudentDetails: r.student_details,
    ForeignKeys: r.foreign_keys,
  }));
};

// Decorate for grid display
const preProcessResult = (data: MappedContact[]) => {
  return data.map((d) => {
    d.id = d._id;
    return d;
  });
};

/**
 * Hook to fetch api data from contact search api
 */
export function useContactSearch() {
  const { callApi, isAuthenticated } = usePymbleApi();

  const [contactSearchFilter, setContactSearchFilter] = useState<ContactFilter>(
    {
      search: "",
      active_only: 1,
      type: ["staff"],
      limit: CONTACT_SEARCH_LIMIT,
      page: 1,
    }
  );

  const { data: contactSearchResult = [], isLoading } = useQuery({
    queryKey: ["contactSearch", contactSearchFilter],
    queryFn: async () => {
      if (!contactSearchFilter.search) return [];

      const types = contactSearchFilter.type || [];
      const body = {
        name: contactSearchFilter.search || "",
        is_staff: types.includes("staff"),
        is_student: types.includes("student"),
        is_carer: types.includes("carer"),
        include_inactive: contactSearchFilter.active_only === 0,
        limit: contactSearchFilter.limit || CONTACT_SEARCH_LIMIT,
        page: Math.max(0, (contactSearchFilter.page || 1) - 1),
      };

      const raw = await callApi("POST", API_END_POINT, { data: body });
      const mapped = mapResponse(raw as S2ContactResult[]);
      return preProcessResult(mapped);
    },
    enabled: isAuthenticated && !!contactSearchFilter.search,
  });

  return {
    contactSearchResult,
    contactSearchResultLoaded: !isLoading,
    contactSearchFilter,
    setContactSearchFilter,
  };
}
